"use client";

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, SupabaseClient } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

// Initialize client outside component to avoid recreation, or inside if strictly needed (usually outside is fine for singleton browser client)
const supabase = createClient();

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'user';
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

type EditableProfileFields = Pick<UserProfile, 'full_name' | 'avatar_url'>;

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  isAdmin: () => boolean;
  updateProfile: (updates: Partial<EditableProfileFields>) => Promise<{ error: Error | null }>;
  deleteAccount: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (userId: string) => {
    try {
      // Let Supabase handle timeouts naturally (uses role-based statement_timeout)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // Structured error logging
        console.error('Error fetching user profile:', {
          type: 'DATABASE_ERROR',
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint || 'Verify NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY in .env',
          userId: userId,
          timestamp: new Date().toISOString()
        });
        setUserProfile(null);
        return;
      }

      setUserProfile(data);
    } catch (error: any) {
      // Categorize error types
      const errorType = (() => {
        if (error.name === 'AbortError' || error.message?.includes('aborted')) {
          return 'FETCH_ABORTED';
        }
        if (error.message?.includes('fetch') || error.message?.includes('network')) {
          return 'NETWORK_ERROR';
        }
        return 'UNKNOWN_ERROR';
      })();

      // Structured error info
      const errorInfo = {
        type: errorType,
        message: error.message || 'Unknown error occurred',
        userId: userId,
        timestamp: new Date().toISOString(),
        ...(error.code && { code: error.code }),
        ...(error.details && { details: error.details }),
      };

      // Log based on error type
      if (errorType === 'FETCH_ABORTED') {
        console.warn('User profile fetch aborted:', errorInfo);
        return;
      } else if (errorType === 'NETWORK_ERROR') {
        console.error('Network error fetching user profile:', {
          ...errorInfo,
          suggestion: 'Check internet connection and verify NEXT_PUBLIC_SUPABASE_URL is correct'
        });
      } else {
        console.error('Unexpected error fetching user profile:', errorInfo);
        console.error('Full error object:', error);
      }

      setUserProfile(null);
    }
  };

  useEffect(() => {
    // Verify Supabase configuration on mount
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      console.error('Missing Supabase configuration:', {
        message: 'Environment variables not found',
        details: 'NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY must be set in .env.local',
        hint: 'Create a .env.local file in the project root with your Supabase credentials',
        code: 'ENV_MISSING'
      });
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.error('Session error:', {
          message: error.message,
          details: error.message,
          hint: 'Verify Supabase URL and anon key are correct',
          code: error.name
        });
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      // Always clear local state for graceful degradation
      setUser(null);
      setUserProfile(null);

      if (error) {
        // Categorize error type
        const errorType = (() => {
          if (error.message?.includes('fetch') || error.message?.includes('network')) {
            return 'NETWORK_ERROR';
          }
          if (error.message?.includes('timeout')) {
            return 'TIMEOUT_ERROR';
          }
          return 'SIGNOUT_ERROR';
        })();

        // Single structured error log
        console.error('Sign out error:', {
          type: errorType,
          message: error.message,
          code: error.name,
          timestamp: new Date().toISOString(),
          hint: 'User signed out locally, but server signout may have failed'
        });

        return { error: error as Error };
      }

      return { error: null };
    } catch (error) {
      // Handle unexpected errors (network failures, etc.)
      // Always clear local state
      setUser(null);
      setUserProfile(null);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error during sign out';
      const errorObject = error instanceof Error ? error : new Error(errorMessage);

      console.error('Unexpected sign out error:', {
        type: 'UNEXPECTED_ERROR',
        message: errorMessage,
        timestamp: new Date().toISOString(),
        hint: 'User signed out locally despite unexpected error'
      });

      return { error: errorObject };
    }
  };

  const isAdmin = () => {
    return user?.app_metadata?.role === 'admin';
  };

  const updateProfile = async (updates: Partial<EditableProfileFields>) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Update local state
      setUserProfile((prev) => (prev ? { ...prev, ...updates } : null));
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const deleteAccount = async () => {
    try {
      if (!user) throw new Error('No user logged in');

      // Attempt to delete profile data
      const { error: profileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) {
        console.error('Error deleting profile:', profileError);
        // Continue to sign out even if profile delete fails (might be RLS restricted)
      }

      const { error: signOutError } = await signOut();
      if (signOutError) {
        return { error: signOutError };
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        loading,
        signIn,
        signUp,
        signOut,
        isAdmin,
        updateProfile,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};